import React, { useState, useRef } from 'react';
import {
  Flex, Text, Box, ActionNote, StateTaxFormInfo,
} from '../../components';
import * as S from '../../styles';
import { STEPS } from '../commonStateTaxData';
import {
  basicInfoQuestions,
  exemptionQuestions_12_22,
  allowanceAndAdditionalAmtQuestions,
} from './data';
import FormConsumer from '../../collect-info/FormConsumers';
// import CollapsibleQuestions from '../CollapsibleQuestions';
import StateTaxFormLayout from '../common/state-tax-content-layout';

function Version2025({
  prefilledData = {},
  saveFormData,
  isSubmitting,
  scrollTop,
  handleSigning,
  viewResultAsManager,
  data,
  companyStaff,
  getPdfUrl,
  getInitialQuestions,
  resetInitialQuestions,
  formLink,
  isOnboardingV2,
}) {
  const [currentStep, setCurrentStep] = useState(STEPS.step1);

  const formatFullName = () => {
    const firstName = `${`${prefilledData.employee_first_name} `}` || '';
    const middleInitial = prefilledData.employee_middle_name;
    const lastName = prefilledData.employee_last_name || '';
    return `${firstName}${middleInitial ? `${middleInitial} ` : ''}${lastName}`;
  };
  prefilledData.employee_name = formatFullName();

  // Basic Info Questions (Line 1, 2, 3)
  const basicInfoForm = useRef();
  const basicInfoInitQ = getInitialQuestions(basicInfoQuestions);
  const [basicInfo, setBasicInfo] = useState(basicInfoInitQ);

  // Total Allowances + Additional Amount Withheld Questions (Line 4 and 5)
  const allowanceAndAdditionalAmtForm = useRef();
  const allowanceAndAdditionalAmtInitQ = getInitialQuestions(allowanceAndAdditionalAmtQuestions);
  const [allowanceAndAdditionalAmt, setAllowanceAndAdditionalAmt] = useState(
    allowanceAndAdditionalAmtInitQ,
  );

  // Exemption Questions - Line 6(a-e)
  const [isExemptFromWithholding, setIsExemptFromWithholding] = useState(
    data.is_exempt || false,
  );
  const exemptionsForm = useRef();
  const exemptionInitQ = getInitialQuestions(exemptionQuestions_12_22);
  const [exemptionStatus, setExemptionStatus] = useState(exemptionInitQ);

  // Tribal Memeber Question - Line 7
  const [isTribalMember, setIsTribalMember] = useState(
    data.is_tribal_member || false,
  );

  const renderBasicInfoForm = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={basicInfo}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = basicInfo.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setBasicInfo(questions);
        }}
        ref={basicInfoForm}
      />
      <Flex column>
        <S.QuestionLabel mb="7px" isRequired>
          I wish to be exempt from withholding.
        </S.QuestionLabel>
        <S.QuestionWrapper mb="20px">
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={isExemptFromWithholding}
                checked={isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              Yes
            </Flex>
          </label>
          <label>
            <Flex alignItems="center">
              <S.Input
                type="radio"
                value={!isExemptFromWithholding}
                checked={!isExemptFromWithholding}
                disabled={isReviewStep}
                width="auto"
                onChange={(e) => {
                  setIsExemptFromWithholding(!isExemptFromWithholding);
                }}
              />
              <Box ml={2} />
              No
            </Flex>
          </label>
        </S.QuestionWrapper>
      </Flex>
    </div>
  );

  const renderAllowanceAndAdditionalAmtQuestions = (isReviewStep) => (
    <div>
      <FormConsumer
        questions={allowanceAndAdditionalAmt}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = allowanceAndAdditionalAmt.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setAllowanceAndAdditionalAmt(questions);
        }}
        ref={allowanceAndAdditionalAmtForm}
      />
    </div>
  );

  const renderExemptForm = (isReviewStep) => (
    <Flex column>
      <FormConsumer
        questions={exemptionStatus}
        {...(isReviewStep && {
          viewResultAsManager: true,
          reviewActions: [],
        })}
        onQuestionChange={({ question, value }) => {
          const questions = exemptionStatus.map((q) => {
            if (q.id !== question.id) return q;
            const answer = {
              ...q.answer,
              answer: {
                ...q.answer.answer,
                [q.id]: value,
              },
            };
            const updatedQuestion = { ...q, answer };
            return updatedQuestion;
          });
          setExemptionStatus(questions);
        }}
        ref={exemptionsForm}
      />
    </Flex>
  );

  const renderTribalExemption = (isReviewStep) => (
    <Flex column>
      <S.QuestionLabel>
        Check this box if you are an enrolled tribal member residing on tribal land in
        Maine and you are claiming an exemption from Maine withholding on the wages, salaries,
        or other compensation derived from or connected with sources on tribal land in Maine
        (that is, wages, salaries, or other compensation
        {' '}
        <strong>for work performed in Maine on tribal land</strong>
        ).
      </S.QuestionLabel>
      <Flex mb="20px">
        <S.Input
          type="checkbox"
          checked={isTribalMember}
          disabled={isReviewStep}
          onChange={() => {
            setIsTribalMember(!isTribalMember);
          }}
        />
      </Flex>
    </Flex>
  );

  const handleValidation = () => {
    // Basic info form validation
    if (basicInfoForm && basicInfoForm.current
      && basicInfoForm.current.validate((error) => !!error)) {
      return true;
    }

    // Exemption form validation
    if (exemptionsForm && exemptionsForm.current
      && exemptionsForm.current.validate((error) => !!error)) {
      return true;
    }

    // Allowance and withholding form validation
    if (allowanceAndAdditionalAmtForm && allowanceAndAdditionalAmtForm.current
      && allowanceAndAdditionalAmtForm.current.validate((error) => !!error)) {
      return true;
    }

    return false;
  };

  const handleSubmission = () => {
    const hasErrors = handleValidation();

    if (hasErrors) return;

    const basicInfoData = basicInfo
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    const exemptionData = { is_exempt: isExemptFromWithholding };
    let exemptionQuestionData = resetInitialQuestions(exemptionQuestions_12_22)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});
    let additionalAmtAndWithholdingData = resetInitialQuestions(allowanceAndAdditionalAmtQuestions)
      .reduce((acc, question) => ({ ...acc, ...question.answer.answer }), {});

    if (isExemptFromWithholding) {
      exemptionQuestionData = exemptionStatus.reduce(
        (acc, question) => ({ ...acc, ...question.answer.answer }),
        {},
      );
    } else {
      additionalAmtAndWithholdingData = allowanceAndAdditionalAmt.reduce(
        (acc, question) => ({ ...acc, ...question.answer.answer }),
        {},
      );
    }

    const tribalMemberData = {
      is_tribal_member: isTribalMember,
    };

    const payload = {
      ...basicInfoData,
      ...exemptionData,
      ...additionalAmtAndWithholdingData,
      ...exemptionQuestionData,
      ...tribalMemberData,
    };

    saveFormData(payload, () => {
      setCurrentStep(STEPS.step2);
      scrollTop();
    });
  };

  const renderStep1 = () => {
    let disabled;
    let disabledText;
    if (viewResultAsManager) {
      disabled = true;
      disabledText = 'This step has already been completed.';
    } else if (companyStaff && companyStaff.id) {
      disabled = true;
      disabledText = 'This step should be completed by the employee.';
    }

    return (
      <Flex column>
        {
          isOnboardingV2 ? null : (
            <Text fontSize="22px" bold>
              Fill state tax form
            </Text>
          )
        }
        <a href={formLink} target="_blank" rel="noreferrer">
          View original form and instructions
        </a>
        <Flex column mt={16}>
          {renderBasicInfoForm()}
          {!isExemptFromWithholding && renderAllowanceAndAdditionalAmtQuestions()}
          {isExemptFromWithholding && renderExemptForm()}
          {renderTribalExemption()}
        </Flex>
        {viewResultAsManager
          && companyStaff.pii_permission === 'full_access' && (
            <Flex column mt={10} mb={10}>
              <Flex alignItems="center">
                <Text mr={16}>Download signed documents</Text>
                <a href={getPdfUrl()} target="_blank" rel="noopener noreferrer">
                  <img src="/j/images/onboarding/download.svg" />
                </a>
              </Flex>
            </Flex>
        )}
        <S.Button
          disabled={disabled}
          onClick={handleSubmission}
          loading={isSubmitting}
        >
          Next
        </S.Button>
        {disabledText && (
          <Flex justifyContent="flex-end">
            <ActionNote text={disabledText} />
          </Flex>
        )}
      </Flex>
    );
  };

  const renderStep2 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Review
      </Text>
      <StateTaxFormInfo />
      <a href={formLink} target="_blank" rel="noreferrer">
        View original form and instructions
      </a>
      <Flex column mt={16}>
        {renderBasicInfoForm(true)}
        {!isExemptFromWithholding && renderAllowanceAndAdditionalAmtQuestions()}
        {isExemptFromWithholding && renderExemptForm(true)}
        {renderTribalExemption(true)}
      </Flex>
      <Flex mt="10px">
        <S.SecondaryButton
          disabled={viewResultAsManager}
          onClick={() => {
            setCurrentStep(STEPS.step1);
            scrollTop();
          }}
        >
          Back
        </S.SecondaryButton>
        <Flex flex={1} column ml={2}>
          <S.Button
            disabled={viewResultAsManager}
            onClick={() => {
              handleSigning();
              setCurrentStep(STEPS.step3);
              scrollTop();
            }}
            loading={isSubmitting}
          >
            Finalize and sign
          </S.Button>
        </Flex>
      </Flex>
    </Flex>
  );

  const renderStep3 = () => (
    <Flex column>
      <Text fontSize="22px" bold>
        Finalize and sign
      </Text>
      <Flex column mt={44}>
        <div id="iframe-container" style={{ height: '640px' }} />
      </Flex>
    </Flex>
  );

  const isSigningStep = currentStep === STEPS.step3;

  return (
    <StateTaxFormLayout isOnboardingV2={isOnboardingV2} isSigningStep={isSigningStep}>
      {currentStep === STEPS.step1 && renderStep1()}
      {currentStep === STEPS.step2 && renderStep2()}
      {currentStep === STEPS.step3 && renderStep3()}
    </StateTaxFormLayout>
  );
}

export default Version2025;
